import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * The library's view, kept in the page's address (P5): which tab, which
 * filters, which range and which page. A reload, the back button and the
 * round trip to Manage Categories all return to the same view, and a tab can
 * be linked. Defaults are left out of the address, and a value outside its
 * vocabulary falls back to the default rather than reaching the backend's
 * 422.
 */

export const PLACEMENTS = ['in', 'not_in'] as const;
export type Placement = (typeof PLACEMENTS)[number] | '';

export const STATUSES = ['DRAFT', 'PUBLISHED', 'INACTIVE', 'ARCHIVED'] as const;
export type Status = (typeof STATUSES)[number] | '';

/** The days views and downloads cover, as the backend accepts them. */
export const RANGES = [7, 30, 90] as const;
export type Range = (typeof RANGES)[number];

export interface DocumentsParams {
  search: string;
  /** A category id, `'all'` or `'uncategorized'`. */
  category: string;
  placement: Placement;
  status: Status;
  range: Range;
  page: number;
}

export const DEFAULTS: DocumentsParams = {
  search: '',
  category: 'all',
  placement: '',
  status: '',
  range: 30,
  page: 1,
};

function oneOf<T extends string | number>(
  value: string | null,
  allowed: readonly T[],
  fallback: T,
): T {
  if (value === null) return fallback;
  const match = allowed.find((candidate) => String(candidate) === value);
  return match ?? fallback;
}

export function parseParams(search: URLSearchParams): DocumentsParams {
  const page = Number(search.get('page'));
  return {
    search: search.get('search') ?? DEFAULTS.search,
    category: search.get('category') || DEFAULTS.category,
    placement: oneOf(search.get('placement'), PLACEMENTS, DEFAULTS.placement),
    status: oneOf(search.get('status'), STATUSES, DEFAULTS.status),
    range: oneOf(search.get('range'), RANGES, DEFAULTS.range),
    page: Number.isInteger(page) && page >= 1 ? page : DEFAULTS.page,
  };
}

export function serializeParams(params: DocumentsParams): URLSearchParams {
  const search = new URLSearchParams();
  for (const key of Object.keys(DEFAULTS) as (keyof DocumentsParams)[]) {
    if (params[key] !== DEFAULTS[key]) search.set(key, String(params[key]));
  }
  return search;
}

/**
 * A new view from a change. Anything but turning the page returns to the
 * first page, because the old page number belongs to the old list.
 */
export function withChange(
  params: DocumentsParams,
  patch: Partial<DocumentsParams>,
): DocumentsParams {
  const pageOnly = Object.keys(patch).every((key) => key === 'page');
  return { ...params, ...(pageOnly ? {} : { page: 1 }), ...patch };
}

export function useDocumentsParams(): [
  DocumentsParams,
  (patch: Partial<DocumentsParams>) => void,
] {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => parseParams(searchParams), [searchParams]);
  const change = useCallback(
    (patch: Partial<DocumentsParams>) => {
      setSearchParams(serializeParams(withChange(parseParams(searchParams), patch)), {
        replace: true,
      });
    },
    [searchParams, setSearchParams],
  );
  return [params, change];
}
